// Перевод ответов сервера в типы приложения.
//
// ПОЧЕМУ ОТДЕЛЬНЫЙ СЛОЙ. Сервер отдаёт заметно больше, чем нужно
// страницам, и называет вещи своими именами: датасет у него `id`, лут
// внутри `lootTables[].entries[].itemDisplayName`, каталог врагов —
// внутри `catalog`. Разбирать это прямо в компонентах значит намертво
// прибить разметку к текущей форме ответа: любое изменение сервера
// пришлось бы искать по шести страницам. Здесь же оно ловится в одном
// месте, а офлайн-образец и сервер дают компонентам ОДИН И ТОТ ЖЕ тип —
// иначе половина интерфейса работала бы только с сервером, а половина
// только без него, и никто бы не заметил, какая именно.

import type { Catalog, DatasetInfo, ItemEntry, LootTable } from '../lib/types';
import type { GroupStat, SpawnStatistics } from '../lib/spawns';
import type { DatasetSummary, Percentiles } from '../lib/stats';
import { METRICS } from '../lib/stats';

const asNumber = (value: unknown, fallback = 0): number => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const asString = (value: unknown): string | null => (value == null ? null : String(value));

export function normalizeDatasets(payload: unknown): DatasetInfo[] {
  const list = Array.isArray(payload)
    ? payload
    : ((payload as { datasets?: unknown[] } | null)?.datasets ?? []);
  return (list as Array<Record<string, unknown>>).map((entry) => ({
    // У сервера ключ датасета — `id` («runs/baseline»), и именно его
    // ждёт обратно ?dataset=. Имя файла для этого не годится.
    name: String(entry.id ?? entry.name ?? entry.dataset ?? entry.file ?? 'без имени'),
    configName: asString(entry.configName),
    // Смешанный датасет отпечатка не имеет — и не должен его выдумывать:
    // раздел 5 контракта запрещает складывать такие строки вместе.
    configHash: entry.mixedConfigs === true ? null : asString(entry.configHash),
    rows: asNumber(entry.rows ?? entry.count ?? entry.lines),
    origin: 'server' as const,
  }));
}

/** Сырые строки телеметрии из ответа сервера: разбирает их наш же парсер. */
export function telemetryRawLines(payload: unknown): string | null {
  if (typeof payload === 'string') return payload;
  if (payload === null || typeof payload !== 'object') return null;
  const record = payload as Record<string, unknown>;
  if (typeof record.raw === 'string') return record.raw;
  for (const key of ['all', 'records', 'entries', 'runs']) {
    const value = record[key];
    if (!Array.isArray(value)) continue;
    const lines = value
      .map((item) => (item as { raw?: unknown }).raw)
      .filter((line): line is string => typeof line === 'string');
    if (lines.length > 0) return lines.join('\n');
  }
  return null;
}

export function normalizeCatalog(enemiesPayload: unknown, lootPayload: unknown): Omit<Catalog, 'origin'> {
  const e = (enemiesPayload ?? {}) as Record<string, unknown>;
  const inner = (e.catalog ?? {}) as Record<string, unknown>;
  const enemies = (Array.isArray(e.enemies)
    ? e.enemies
    : Array.isArray(inner.enemies)
      ? inner.enemies
      : []) as Catalog['enemies'];

  const l = (lootPayload ?? {}) as Record<string, unknown>;

  const lootTables: LootTable[] = (Array.isArray(l.lootTables) ? l.lootTables : []).map(
    (raw: Record<string, unknown>) => {
      const entries = (Array.isArray(raw.entries) ? raw.entries : []).map(
        (entry: Record<string, unknown>) => ({
          guid: String(entry.guid ?? ''),
          itemAssetPath: asString(entry.itemPath ?? entry.itemAssetPath),
          itemId: asString(entry.itemId),
          itemName: asString(entry.itemDisplayName ?? entry.itemName),
          weight: asNumber(entry.weight),
          share: asNumber(entry.share),
          missing: entry.resolved === false || entry.missing === true,
          stub: entry.itemPlaceholder === true || entry.itemTest === true || entry.stub === true,
        }),
      );
      return {
        assetPath: String(raw.path ?? raw.assetPath ?? ''),
        assetName: String(raw.name ?? raw.assetName ?? ''),
        stub: raw.placeholder === true || raw.test === true || raw.stub === true,
        totalWeight: asNumber(raw.totalWeight, entries.reduce((s, x) => s + x.weight, 0)),
        entries,
      };
    },
  );

  const items: ItemEntry[] = (Array.isArray(l.items) ? l.items : []).map(
    (raw: Record<string, unknown>) => {
      const shape = (raw.shape ?? null) as { width?: unknown; height?: unknown } | null;
      return {
        assetPath: String(raw.path ?? raw.assetPath ?? ''),
        assetName: String(raw.name ?? raw.assetName ?? ''),
        id: asString(raw.id),
        displayName: asString(raw.displayName),
        kind: String(raw.kindName ?? raw.kind ?? ''),
        stackable: raw.stackable === true,
        stackLimit: asNumber(raw.stackLimit),
        width: asNumber(shape?.width ?? raw.width),
        height: asNumber(shape?.height ?? raw.height),
        weightGrams: asNumber(raw.weightGrams),
        healAmount: asNumber(raw.healAmount),
        isInfected: raw.isInfected === true,
        stub: raw.placeholder === true || raw.test === true || raw.stub === true,
      };
    },
  );

  return {
    enemies,
    assetsFound: asNumber(e.assetsFound ?? inner.assetsFound),
    dropsLoot: (e.dropsLoot ?? inner.dropsLoot) === true,
    // Фразу про дроп каталог формулирует сам (схема 2). Показывается
    // дословно: пересказ теряет ровно то, ради чего её и читают —
    // что именно решается при генерации, а что в момент убийства.
    dropsNote: asString(e.dropsNote ?? inner.dropsNote),
    lootTables,
    lootFromServer: lootTables.length > 0,
    items,
    warnings: (Array.isArray(l.warnings) ? l.warnings : []).map((w: Record<string, unknown>) =>
      String(w.text ?? w),
    ),
  };
}

interface ServerGroup {
  key: unknown;
  rooms?: unknown;
  enemies?: unknown;
  lootPlacements?: unknown;
  lootByKind?: Record<string, unknown>;
  cells?: unknown;
  enemiesPerRoom?: unknown;
  lootPerRoom?: unknown;
  enemyRoomShare?: unknown;
}

const sumLoot = (byKind: Record<string, unknown> | undefined): number =>
  byKind ? Object.values(byKind).reduce<number>((sum, v) => sum + asNumber(v), 0) : 0;

const toGroup = (raw: ServerGroup): GroupStat => ({
  key: String(raw.key),
  rooms: asNumber(raw.rooms),
  enemies: asNumber(raw.enemies),
  lootPlacements: asNumber(raw.lootPlacements),
  lootAmount: sumLoot(raw.lootByKind),
  enemiesPerRoom: asNumber(raw.enemiesPerRoom),
  lootPerRoom: asNumber(raw.lootPerRoom),
  roomsWithEnemyShare: asNumber(raw.enemyRoomShare),
  cells: asNumber(raw.cells),
});

/** Ответ /spawns у сервера уже сведён — берём его, а не пересчитываем сами. */
export function normalizeSpawns(payload: unknown): SpawnStatistics | null {
  const root = (payload ?? {}) as Record<string, unknown>;
  const s = (root.spawns ?? root) as Record<string, unknown>;
  if (!Array.isArray(s.byDistance)) return null;

  const byDistance = (s.byDistance as ServerGroup[]).map((raw) => ({
    distance: asNumber(raw.key),
    rooms: asNumber(raw.rooms),
    enemies: asNumber(raw.enemies),
    lootPlacements: asNumber(raw.lootPlacements),
    lootAmount: sumLoot(raw.lootByKind),
    enemiesPerRoom: asNumber(raw.enemiesPerRoom),
    lootPerRoom: asNumber(raw.lootPerRoom),
  }));

  const stacks = Array.isArray(s.lootStacks) ? (s.lootStacks as Array<Record<string, unknown>>) : [];
  const totalAmount = stacks.reduce((sum, x) => sum + asNumber(x.total), 0);
  const entrance = (s.entrance ?? {}) as Record<string, unknown>;
  const exit = (s.exit ?? {}) as Record<string, unknown>;
  const unreachable = (s.unreachable ?? {}) as Record<string, unknown>;

  const facts = (s.facts ?? {}) as Record<string, unknown>;

  return {
    factsNote: asString(facts.note),
    seeds: asNumber(s.runsCounted ?? s.runs),
    rooms: asNumber(s.rooms),
    byKind: (Array.isArray(s.byRoomKind) ? (s.byRoomKind as ServerGroup[]) : []).map(toGroup),
    bySize: (Array.isArray(s.bySizeClass) ? (s.bySizeClass as ServerGroup[]) : []).map(toGroup),
    byDistance,
    lootByKind: stacks.map((x) => ({
      kind: String(x.kind),
      placements: asNumber(x.placements),
      amount: asNumber(x.total),
      share: totalAmount > 0 ? asNumber(x.total) / totalAmount : 0,
    })),
    // Сервер считает недостижимость по прогонам, а не по комнатам:
    // берём число прогонов, а список seed показываем как есть.
    unreachableRooms: asNumber(unreachable.runs),
    unreachableSeeds: (Array.isArray(unreachable.seeds) ? unreachable.seeds : []).map((v) =>
      asNumber(v),
    ),
    enemiesInEntrance: asNumber(entrance.withEnemies),
    enemiesInExit: asNumber(exit.withEnemies),
    maxDistance: byDistance.reduce((max, band) => Math.max(max, band.distance), 0),
  };
}

// --- ответ POST /simulate и GET /stats --------------------------------------

/** Ключи величин у сервера — с группой: `population.enemies`. */
const SERVER_METRIC_KEYS: Record<string, string> = {
  enemies: 'population.enemies',
  ammoTotal: 'population.ammoTotal',
  ammoMargin: 'economy.ammoMargin',
  ammoSpent: 'economy.ammoSpent',
  medkits: 'population.medkits',
  rooms: 'layout.rooms',
  doors: 'layout.doors',
  floorCells: 'layout.floorCells',
  lootDrops: 'population.lootDrops',
  attempts: 'layout.attempts',
};

const EMPTY: Percentiles = {
  count: 0, min: NaN, p1: NaN, p10: NaN, p25: NaN, p50: NaN,
  p75: NaN, p90: NaN, p99: NaN, max: NaN, mean: NaN, mad: NaN,
};

function toPercentiles(raw: Record<string, unknown> | undefined): Percentiles {
  if (!raw) return EMPTY;
  const scale = (raw.scale ?? {}) as Record<string, unknown>;
  return {
    count: asNumber(raw.count),
    min: asNumber(raw.min, NaN),
    p1: asNumber(raw.p1, NaN),
    p10: asNumber(raw.p10, NaN),
    p25: asNumber(raw.p25, NaN),
    p50: asNumber(raw.p50 ?? raw.median, NaN),
    p75: asNumber(raw.p75, NaN),
    p90: asNumber(raw.p90, NaN),
    p99: asNumber(raw.p99, NaN),
    max: asNumber(raw.max, NaN),
    mean: asNumber(raw.mean, NaN),
    mad: asNumber(scale.mad ?? scale.value, NaN),
  };
}

export interface SimulateSummary {
  summary: DatasetSummary;
  /** Четыре вида находок, если сервер их отдал. */
  findings: ServerFindings | null;
  /** Готовые фразы аномалий от сервера: он их уже сформулировал по-русски. */
  anomalyPhrases: string[];
  anomalyTotal: number;
  anomalyTruncated: boolean;
  durationMs: number | null;
  configHash: string | null;
  cacheHit: boolean;
}

/**
 * Ответ /simulate и /stats — СВОДКА, а не строки прогонов.
 *
 * Из-за этого пересчёт ползунком перерисовывает обзор, но не таблицы
 * по каждому seed: строк там просто нет, и подставлять вместо них
 * прежний датасет молча было бы подлогом. Страницы, которым нужны
 * строки, честно говорят, что показывают датасет.
 */
export function normalizeSimulate(payload: unknown): SimulateSummary {
  const root = (payload ?? {}) as Record<string, unknown>;
  const s = (root.summary ?? {}) as Record<string, unknown>;
  const numeric = (s.numeric ?? {}) as Record<string, Record<string, unknown>>;
  const boolean = (s.boolean ?? {}) as Record<string, Record<string, unknown>>;
  const anomalies = (root.anomalies ?? {}) as Record<string, unknown>;

  const metrics = new Map<string, Percentiles>();
  for (const metric of METRICS) {
    metrics.set(metric.key, toPercentiles(numeric[SERVER_METRIC_KEYS[metric.key] ?? metric.key]));
  }

  const runs = asNumber(s.runs);
  const share = (key: string): number => asNumber(boolean[key]?.share);
  const flags = (Array.isArray(s.flags) ? s.flags : []) as Array<Record<string, unknown>>;

  const summary: DatasetSummary = {
    rows: runs,
    configHash: asString(root.configHash),
    configHashes: root.configHash == null ? [] : [String(root.configHash)],
    brokenShare: runs > 0 ? asNumber(s.flaggedRuns) / runs : 0,
    brokenSeeds: [],
    // respectsAmmoDeficit — доля соблюдённых, избыток это остаток.
    ammoSurplusShare: 1 - share('economy.respectsAmmoDeficit'),
    layoutFallbackShare: share('layout.usedFallback'),
    noEnemiesShare:
      runs > 0
        ? asNumber(flags.find((f) => String(f.flag ?? f.name) === 'no_enemies')?.count) / runs
        : 0,
    cellCollisionShare:
      runs > 0
        ? asNumber(flags.find((f) => String(f.flag ?? f.name) === 'cell_collisions')?.count) / runs
        : 0,
    relaxedClearanceShare: share('diagnostics.relaxedEnemyClearance'),
    flagCounts: flags.map((f) => ({
      flag: String(f.flag ?? f.name ?? ''),
      count: asNumber(f.count ?? f.runs),
    })),
    metrics,
  };

  const items = (Array.isArray(anomalies.items) ? anomalies.items : []) as Array<
    Record<string, unknown>
  >;

  return {
    summary,
    findings: normalizeFindings(payload),
    anomalyPhrases: items
      .map((item) => asString(item.explanation))
      .filter((text): text is string => text !== null),
    anomalyTotal: asNumber(anomalies.total),
    anomalyTruncated: anomalies.truncated === true,
    durationMs: root.durationMs == null ? null : asNumber(root.durationMs),
    configHash: asString(root.configHash),
    cacheHit: root.cacheHit === true,
  };
}

// --- findings: четыре вида находок от сервера --------------------------------
//
// Опираемся на поле `classification` каждой метрики, а НЕ на то, в каком
// массиве она лежит. Классификация зависит от выборки: врагов на baseline
// шесть различных значений и это частотная таблица, а на denser-enemies
// двенадцать, и та же метрика уходит в детектор выбросов. Читать это по
// расположению в массиве значило бы завести второй источник правды,
// который разойдётся с сервером на первой же правке порога.

export type MetricClassification = 'distribution' | 'outliers' | 'rareEvent' | 'excluded' | string;

export interface ClassificationEntry {
  metric: string;
  label: string;
  classification: MetricClassification;
  block: string | null;
  distinct: number;
  /** Может ли метрика сама привести seed в список выбросов. */
  grounds: boolean;
  reason: string;
}

export interface DistributionValue {
  value: number;
  runs: number;
  share: number;
  rare: boolean;
  seeds: number[];
}

export interface DistributionFinding {
  metric: string;
  label: string;
  distinct: number;
  min: number;
  max: number;
  mode: number;
  modeShare: number;
  values: DistributionValue[];
  rareValues: number;
  /** Готовая фраза сервера. Показывается дословно. */
  explanation: string;
}

export interface RareEventFinding {
  metric: string;
  label: string;
  runs: number;
  share: number;
  total: number;
  max: number;
  seeds: number[];
  explanation: string;
}

export interface OutlierItem {
  seed: number;
  severity: number;
  explanation: string;
  reasons: Array<{ metric: string; label: string; value: number; median: number; phrase: string }>;
}

export interface ServerFindings {
  classification: ClassificationEntry[];
  flags: Array<{ flag: string; count: number }>;
  rareEvents: RareEventFinding[];
  distributions: DistributionFinding[];
  outliers: {
    madFactor: number;
    total: number;
    share: number;
    returned: number;
    truncated: number;
    items: OutlierItem[];
    note: string | null;
  };
  excluded: Array<{ metric: string; label: string; reason: string; value: number | null }>;
}

const arr = (value: unknown): Array<Record<string, unknown>> =>
  Array.isArray(value) ? (value as Array<Record<string, unknown>>) : [];

/** Разбирает поле `findings`. Возвращает null, если сервер его ещё не отдаёт. */
export function normalizeFindings(payload: unknown): ServerFindings | null {
  const root = (payload ?? {}) as Record<string, unknown>;
  const f = root.findings as Record<string, unknown> | undefined;
  if (!f || typeof f !== 'object') return null;

  const outliers = (f.outliers ?? {}) as Record<string, unknown>;

  return {
    classification: arr(f.classification).map((e) => ({
      metric: String(e.metric ?? ''),
      label: String(e.label ?? e.metric ?? ''),
      classification: String(e.classification ?? 'excluded'),
      block: e.block == null ? null : String(e.block),
      distinct: asNumber(e.distinct),
      grounds: e.grounds === true,
      reason: String(e.reason ?? ''),
    })),
    flags: arr(f.flags).map((e) => ({
      flag: String(e.flag ?? e.name ?? ''),
      count: asNumber(e.count ?? e.runs),
    })),
    rareEvents: arr(f.rareEvents).map((e) => ({
      metric: String(e.metric ?? ''),
      label: String(e.label ?? ''),
      runs: asNumber(e.runs),
      share: asNumber(e.share),
      total: asNumber(e.total),
      max: asNumber(e.max),
      seeds: ((e.seeds as number[]) ?? []).map((s) => asNumber(s)),
      explanation: String(e.explanation ?? ''),
    })),
    distributions: arr(f.distributions).map((e) => ({
      metric: String(e.metric ?? ''),
      label: String(e.label ?? ''),
      distinct: asNumber(e.distinct),
      min: asNumber(e.min),
      max: asNumber(e.max),
      mode: asNumber(e.mode),
      modeShare: asNumber(e.modeShare),
      values: arr(e.values).map((v) => ({
        value: asNumber(v.value),
        runs: asNumber(v.runs),
        share: asNumber(v.share),
        rare: v.rare === true,
        seeds: ((v.seeds as number[]) ?? []).map((s) => asNumber(s)),
      })),
      rareValues: asNumber(e.rareValues),
      explanation: String(e.explanation ?? ''),
    })),
    outliers: {
      madFactor: asNumber(outliers.madFactor),
      total: asNumber(outliers.total),
      share: asNumber(outliers.share),
      returned: asNumber(outliers.returned),
      truncated: asNumber(outliers.truncated),
      items: arr(outliers.items).map((item) => ({
        seed: asNumber(item.seed),
        severity: asNumber(item.severity),
        explanation: String(item.explanation ?? ''),
        reasons: arr(item.reasons).map((r) => ({
          metric: String(r.metric ?? ''),
          label: String(r.label ?? ''),
          value: asNumber(r.value),
          median: asNumber(r.median),
          phrase: String(r.phrase ?? ''),
        })),
      })),
      note: outliers.note == null ? null : String(outliers.note),
    },
    excluded: arr(f.excluded).map((e) => ({
      metric: String(e.metric ?? ''),
      label: String(e.label ?? ''),
      reason: String(e.reason ?? ''),
      value: e.value == null ? null : asNumber(e.value),
    })),
  };
}

// --- сравнение классификаций между выборками ---------------------------------

export interface ClassificationChange {
  metric: string;
  label: string;
  classificationA: MetricClassification;
  classificationB: MetricClassification;
  blockA: string | null;
  blockB: string | null;
  distinctA: number;
  distinctB: number;
  changed: boolean;
  /** Готовая фраза сервера про смену блока. */
  phrase: string | null;
}

export interface ComparisonExtras {
  changes: ClassificationChange[];
  metrics: ClassificationChange[];
  /**
   * Предупреждение о несравнимости числа выбросов: в детектор двух
   * выборок попали разные метрики. Показывается дословно.
   */
  outlierWarning: string | null;
  comparable: boolean;
  onlyInA: string[];
  onlyInB: string[];
  sameConfig: boolean;
}

/** Разбирает ответ /compare. Возвращает null, если сервер его не дал. */
export function normalizeComparison(payload: unknown): ComparisonExtras | null {
  const root = (payload ?? {}) as Record<string, unknown>;
  const c = root.classification as Record<string, unknown> | undefined;
  if (!c || typeof c !== 'object') return null;

  const detector = (c.outlierDetector ?? {}) as Record<string, unknown>;
  const toChange = (e: Record<string, unknown>): ClassificationChange => ({
    metric: String(e.metric ?? ''),
    label: String(e.label ?? ''),
    classificationA: String(e.classificationA ?? ''),
    classificationB: String(e.classificationB ?? ''),
    blockA: e.blockA == null ? null : String(e.blockA),
    blockB: e.blockB == null ? null : String(e.blockB),
    distinctA: asNumber(e.distinctA),
    distinctB: asNumber(e.distinctB),
    changed: e.changed === true,
    phrase: e.phrase == null ? null : String(e.phrase),
  });

  return {
    changes: arr(c.changes).map(toChange),
    metrics: arr(c.metrics).map(toChange),
    outlierWarning: detector.warning == null ? null : String(detector.warning),
    comparable: detector.comparable === true,
    onlyInA: ((detector.onlyInA as string[]) ?? []).map(String),
    onlyInB: ((detector.onlyInB as string[]) ?? []).map(String),
    sameConfig: root.sameConfig === true,
  };
}
